import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text,
  VStack,
  useBreakpointValue
} from '@chakra-ui/react';
import { MdOutlineKeyboardDoubleArrowLeft } from 'react-icons/md';
import ContentLayout from '@layouts/ContentLayout';
import OnHoldFulfillments from '@views/GomsOrderDashboard/OnHoldFulfillments';
import { generalConfig as config, canShowForceRelease } from '@views/GomsOrderDashboard/utils';
import { SCREEN_STYLING_CONSTANTS } from '@views/GomsOrderDashboard/constant';
import GomsFulfillmentDetails from '@components/GomsFulfillmentDetails';
import UserTransfers from '@views/User/UserTransfers';
import OrderDetailsCard from '@components/OrderDetailsCard';
import { ORDER_RESOURCE, ROLES } from '@constants';
import ForceInitiateFulfillment from '@components/ForceInitiateFulfillment';
import useAuthRole from '@hooks/useAuthRole';


const GomsOrderDashboard = () => {
  const dispatch = useDispatch();
  const { hasAnyAuthority } = useAuthRole();

  const { data: configData,  isLoading: isConfigLoading, error: configError} = useSelector(
    (state) => state.gomsOrderDashboard.config
  );

  const [selectedFulfillment, setSelectedFulfillment] = useState(null);
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedRecentOrder, setSelectedRecentOrder] = useState(null);
  const isMobile = useBreakpointValue({ base: true, lg: false });

  // Memoized grid template
  const gridTemplateColumns = useMemo(() => ({
    base: '1fr',
    lg: showRightSidebar ? SCREEN_STYLING_CONSTANTS.GRID_RATIOS.EXPANDED : SCREEN_STYLING_CONSTANTS.GRID_RATIOS.COLLAPSED
  }), [showRightSidebar]);

  useEffect(() => {
    if (!configData && !isConfigLoading && !configError) {
      fetchDashboardConfig();
    }
  }, [dispatch, configData, isConfigLoading, configError]);

  useEffect(() => {
    if (isMobile && !selectedFulfillment && showRightSidebar) {
      setShowRightSidebar(false);
    }
  }, [isMobile, selectedFulfillment, showRightSidebar]);

  const fetchDashboardConfig = useCallback(() => {
    dispatch(
      config.actions.getDashboardConfig()
    );
  }, [dispatch]);

  const handleToggleSidebar = useCallback((e) => {
    e?.preventDefault();
    setShowRightSidebar(prev => !prev);
  }, []);

  const handleFulfillmentSelect = useCallback((fulfillment) => {
    setSelectedFulfillment(fulfillment);

    if (!isMobile && fulfillment) {
      setShowRightSidebar(true);
    }
  }, [isMobile]);

  const handleFulfillmentDeselect = useCallback(() => {
    setSelectedFulfillment(null);

    if (showRightSidebar) {
      setShowRightSidebar(false);
    }
  }, [showRightSidebar]);

  const handleRetryConfig = useCallback(() => {
    fetchDashboardConfig();
  }, [fetchDashboardConfig]);

  const triggerRefreshFulfillmentDashboard = useCallback(() => {
    dispatch(config.actions.getDashboardConfig());
    setSelectedFulfillment(null);
    setShowRightSidebar(false);
  }, [dispatch]);

  const openRecentOrders = useCallback(({ userId, orderId }) => {
    setSelectedOrder({ userId, orderId });
  }, []);

  const handleRecentOrderCardClick = useCallback(({ orderId }) => {
    setSelectedRecentOrder(orderId);
  }, []);

  // Render functions
  const renderConfigLoadingState = () => (
    <Box
      p={6}
      borderRadius="xl"
      w="full"
      bg="backGround"
      borderWidth="1px"
      borderColor="whiteAlpha.300"
    >
      <Flex justifyContent="center" alignItems="center" w="full" h="200px">
        <VStack spacing={4}>
          <Spinner thickness="4px" size="lg" color="colorPrimary" />
          <Text color="whiteAlpha.600" fontSize="sm">
            Loading dashboard configuration...
          </Text>
        </VStack>
      </Flex>
    </Box>
  );

  const renderConfigErrorState = () => (
    <Box
      p={6}
      bg="backGround"
      border="1px solid"
      borderColor="red.300"
      borderRadius="xl"
    >
      <VStack spacing={4} align="stretch">
        <Text color="red.500" fontSize="sm" fontWeight="medium">
          Failed to load dashboard configuration
        </Text>
        <Text color="red.400" fontSize="xs">
          {configError || 'An unknown error occurred'}
        </Text>
        <Button
          size="sm"
          colorScheme="red"
          variant="outline"
          onClick={handleRetryConfig}
          alignSelf="flex-start"
        >
          Retry
        </Button>
      </VStack>
    </Box>
  );

  const renderMainContent = () => {
    if (isConfigLoading) return renderConfigLoadingState();
    if (configError) return renderConfigErrorState();

    return (
      <OnHoldFulfillments
        setShowRightSidebar={setShowRightSidebar}
        selectedGomsFulfillment={selectedFulfillment}
        setSelectedGomsFulfillment={handleFulfillmentSelect}
        onFulfillmentDeselect={handleFulfillmentDeselect}
        onRecentTransfersClick={openRecentOrders}
      />
    );
  };

  const renderSidebarToggle = () => (
    <Box alignItems="center" position="relative">
      <Divider
        style={{ margin: '0' }}
        borderColor="whiteAlpha.300"
        orientation="vertical"
        transition={`width ${SCREEN_STYLING_CONSTANTS.SIDEBAR_ANIMATION_DURATION} ease-in-out`}
      />
      <Button
        colorScheme="brand"
        size="sm"
        bg="black"
        border="2px solid"
        borderColor="whiteAlpha.500"
        borderRadius="xl"
        p={0}
        onClick={handleToggleSidebar}
        position="absolute"
        top={12}
        left={-4}
        aria-label={showRightSidebar ? 'Close sidebar' : 'Open sidebar'}
        title={showRightSidebar ? 'Close sidebar' : 'Open sidebar'}
        _hover={{
          borderColor: 'whiteAlpha.700',
          transform: 'scale(1.05)'
        }}
        transition="all 0.2s ease-in-out"
      >
        <Box
          as="span"
          transition={`transform ${SCREEN_STYLING_CONSTANTS.SIDEBAR_ANIMATION_DURATION} ease-in-out`}
          transform={showRightSidebar ? 'rotate(180deg)' : 'rotate(0deg)'}
          display="inline-block"
        >
          <MdOutlineKeyboardDoubleArrowLeft />
        </Box>
      </Button>
    </Box>
  );

  const renderSidebar = () => (
    <VStack
      overflowX="hidden"
      overflowY="auto"
      h={{ lg: '90vh' }}
      alignItems="stretch"
      gap={4}
      opacity={showRightSidebar ? 1 : 0}
      transition={`opacity ${SCREEN_STYLING_CONSTANTS.SIDEBAR_ANIMATION_DURATION} ease-in-out`}
    >
      {selectedRecentOrder === null && (
        <VStack w={'100%'} gap={4} p={6}>
          {selectedFulfillment && (
            <VStack w={'100%'} gap={2}>
              <GomsFulfillmentDetails
                fulfillmentData={selectedFulfillment}
                onClose={handleFulfillmentDeselect}
                triggerRefreshFulfillmentDashboard={triggerRefreshFulfillmentDashboard}
              />

              {/* Force Initiate Fulfillment Component */}
              {hasAnyAuthority(ROLES.TRANSFER_COMPLIANCE_MGR_WRITE) &&
                canShowForceRelease(selectedFulfillment) && (
                  <ForceInitiateFulfillment
                    fulfillmentData={selectedFulfillment}
                    onSuccess={triggerRefreshFulfillmentDashboard}
                  />
                )}
            </VStack>
          )}

          {selectedOrder?.userId && (
            <VStack w={'100%'} gap={2}>
              <Heading
                px={4}
                color={'white'}
                fontSize={'1.3rem'}
                alignSelf={'start'}
              >
                Recent Transfers
              </Heading>
              <UserTransfers
                isSidebar
                userId={selectedOrder.userId}
                setSelectedOrder={handleRecentOrderCardClick}
                maxW={'auto'}
                orderResource={ORDER_RESOURCE.GOMS_SERVICE}
                orderType={selectedFulfillment?.product}
              />
            </VStack>
          )}

          {!selectedFulfillment && (
            <Box
              p={8}
              textAlign="center"
              color="whiteAlpha.500"
              fontSize="sm"
            >
              Select a fulfillment to view details
            </Box>
          )}
        </VStack>
      )}

      {selectedOrder?.userId && selectedRecentOrder && (
        <VStack w={'100%'} alignItems={'stretch'}>
          <OrderDetailsCard
            onGoBackClick={() => setSelectedRecentOrder(null)}
            orderId={selectedRecentOrder}
            userId={selectedOrder?.userId}
            orderResource={ORDER_RESOURCE.GOMS_SERVICE}
            showNavLink={true}
            showGoBackBtn={true}
          />
        </VStack>
      )}
    </VStack>
  );

  return (
    <ContentLayout>
      <Grid gridTemplateColumns={gridTemplateColumns}>
        {/* Main Column */}
        <VStack
          overflowY="hidden"
          overflowX="hidden"
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems="stretch"
          gap={4}
        >
          <Flex justify="space-between" align="center">
            <Heading size="lg" color="colorPrimary">
              GOMS Order Dashboard
            </Heading>

            {/* Show selected fulfillment indicator on mobile */}
            {isMobile && selectedFulfillment && (
              <Text fontSize="xs" color="whiteAlpha.600">
                Selected: {selectedFulfillment.fulfillmentId}
              </Text>
            )}
          </Flex>

          {renderMainContent()}
        </VStack>

        {/* Sidebar Toggle */}
        {!isMobile && renderSidebarToggle()}

        {/* Right Sidebar */}
        {!isMobile && renderSidebar()}
      </Grid>

      {/* Mobile Modal/Drawer for sidebar content */}
      {isMobile && selectedFulfillment && showRightSidebar && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.800"
          zIndex={1000}
          onClick={handleFulfillmentDeselect}
        >
          <Box
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            w="90%"
            maxW="400px"
            bg="backGround"
            overflowY="auto"
            onClick={(e) => e.stopPropagation()}
          >
            {renderSidebar()}
          </Box>
        </Box>
      )}
    </ContentLayout>
  );
};

export default GomsOrderDashboard;